import React, { useMemo, useCallback } from 'react'
import { getSecondWellIsOpen } from '../../store/slices/appSlice';
import {
	LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import useMediaQuery from '../../store/hooks/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks/hooks';
import { SINGLE_RESPONSIVE_CONTAINER_MIN_SIZE } from '../../constants/constants';


const VLPChart: React.FC = () => {
	const isMobile = useMediaQuery('(max-width: 900px)');
	const secondLine = useAppSelector(getSecondWellIsOpen);
	const well1Results = useAppSelector(state => state.vlpCalculator.well1.results);
	const well2Results = useAppSelector(state => state.vlpCalculator.well2.results);

	const isEmpty = (!well1Results?.p || well1Results.p.length === 0) && (!well2Results?.q || well2Results.q.length === 0);
	const { minX, maxX, minY, maxY, xTicks, yTicks } = useMemo(() => {
		if (isEmpty) {
			const minX = 0, maxX = 700, minY = 0, maxY = 350;
			const xTicks = Array.from({ length: 5 }, (_, i) => minX + (maxX - minX) * i / 4);
			const yTicks = Array.from({ length: 5 }, (_, i) => minY + (maxY - minY) * i / 4);
			return { minX, maxX, minY, maxY, xTicks, yTicks };
		}

		const minX = Math.min(...[...(well1Results?.q || []), ...(well2Results?.q || [])]);
		const maxX = Math.max(...[...(well1Results?.q || []), ...(well2Results?.q || [])]);
		const minY = Math.min(...[...(well1Results?.p || []), ...(well2Results?.p || [])]);
		const maxY = Math.max(...[...(well1Results?.p || []), ...(well2Results?.p || [])]);
		const xTicks = Array.from({ length: 5 }, (_, i) => minX + (maxX - minX) * i / 4);
		const yTicks = Array.from({ length: 5 }, (_, i) => minY + (maxY - minY) * i / 4);
		return { minX, maxX, minY, maxY, xTicks, yTicks };
	}, [well1Results, well2Results, isEmpty]);

	const { t } = useTranslation('ipr');
	// const tooltipLabelFormatter = useCallback((value: number) => value?.toFixed(3), []);
	// const tooltipFormatter = useCallback((value: number) => [value?.toFixed(3)], []);

	const data1 = useMemo(() => {
		if (!well1Results || !well1Results.p || !well1Results.q) {
			return [];
		}
		return well1Results.p.map((p, index) => ({ x: well1Results.q[index], y: p }));
	}, [well1Results]);

	const data2 = useMemo(() => {
		if (!well2Results || !well2Results.p || !well2Results.q) {
			return [];
		}
		return well2Results.p.map((p, index) => ({ x: well2Results.q[index], y: p }));
	}, [well2Results]);

	const renderLegend = useCallback((props: any) => {
		const { payload } = props;
		return (
			<ul style={{
				display: 'flex',
				flexDirection: isMobile ? 'row' : 'column',
				flexWrap: 'wrap',
				gap: '8px',
				listStyle: 'none',
				margin: 0,
				padding: 0,
				justifyContent: isMobile ? 'center' : 'flex-start',
			}}>
				{payload.flatMap((entry: any, index: number) => {
					return [
						<li key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 14 }}>
							<svg width="28" height="14" style={{ flexShrink: 0 }}>
								<line
									x1={0} y1={7} x2={28} y2={7}
									stroke={entry.color}
									strokeWidth={entry.payload?.strokeWidth ?? 2}
									strokeDasharray={entry.payload?.strokeDasharray}
								/>
							</svg>
							<span>{entry.value}</span>
						</li>
					];
				})}
			</ul>
		);
	}, [isMobile]);

	return (
		<div className="vlp__chart_container round-corner-container">
			{/* {(isLoading || isEmpty) && (
				<div className="ipr__chart-overlay">
					<span>{isLoading ? tCommon('loading') : tCommon('no_data')}</span>
				</div>
			)} */}
			<ResponsiveContainer width="100%" height={300} {...SINGLE_RESPONSIVE_CONTAINER_MIN_SIZE}>
				<LineChart data={isEmpty ? [{ x: minX, y: minY }] : []} margin={{
					top: 20,
					right: 30,
					left: 25,
					bottom: 40
				}}>
					<CartesianGrid strokeDasharray="0"
						horizontal={true}
						vertical={true}
					/>
					<XAxis
						axisLine={true}
						dataKey="x"
						domain={[minX, maxX]}
						label={{ value: t('chart_labels.x'), position: 'insideBottom', offset: -10 }}
						type="number"
						ticks={xTicks}
						tickFormatter={(value: number) => value.toFixed(0)}
					/>
					<YAxis
						axisLine={true}
						domain={[minY, maxY]}
						ticks={yTicks}
						label={{ value: t('chart_labels.y'), angle: -90, position: 'insideLeft', offset: -17 }}
						tickFormatter={(value: number) => value > 1000 ? value.toExponential(2) : value.toFixed(2)}
					/>
					<Tooltip
						// labelFormatter={tooltipLabelFormatter}
						// formatter={tooltipFormatter}
						wrapperStyle={{ pointerEvents: 'none' }}
						isAnimationActive={false}
					/>
					<Legend
						content={renderLegend}
						layout={isMobile ? 'horizontal' : 'vertical'}
						verticalAlign={isMobile ? 'bottom' : 'top'}
						align={isMobile ? 'center' : 'right'}
					/>

					<Line
						type="monotone"
						data={data1}
						dataKey="y"
						stroke={isEmpty ? "transparent" : "#4c23deff"}
						strokeWidth={3}
						name={t('ipr:well1_title')}
						dot={true}
						activeDot={false}
						legendType="plainline"
					/>

					{secondLine &&
						<Line
							type="monotone"
							data={data2}
							dataKey="y"
							stroke={isEmpty ? "transparent" : "#e8732aff"}
							strokeWidth={3}
							strokeDasharray="8 4"
							name={t('ipr:well2_title')}
							dot={false}
							activeDot={false}
							legendType="plainline"
						/>
					}
				</LineChart>
			</ResponsiveContainer>
		</div >
	)
}

export default React.memo(VLPChart)